import axios from 'axios'
import { KycListItem, KycSensitiveDetails } from '../../domain/entities/Kyc'
import { GetKycFilters, GetKycResponse, KycDocType, KycRepository } from '../../domain/repositories/KycRepository'
import { kycApiDatasource, KycApiResponse } from '../datasources/KycApiDatasource'

const extractErrorMessage = (err: unknown, fallback: string): string => {
  if (axios.isAxiosError(err)) {
    const data = err.response?.data as { message?: string; error?: string } | undefined
    return data?.message || data?.error || fallback
  }

  return err instanceof Error ? err.message : fallback
}

const toDomainKyc = (k: KycApiResponse): KycListItem => ({
  id: k.id,
  userId: k.user_id,
  businessName: k.business_name,
  proprietorName: k.proprietor_name,
  phone: k.phone,
  email: k.email,
  panLast4: k.pan_last4,
  gstinLast4: k.gstin_last4,
  hasPanDoc: k.has_pan_doc,
  hasGstinDoc: k.has_gstin_doc,
  verified: k.verified,
  verifiedBy: k.verified_by,
  verifiedAt: k.verified_at,
  rejected: k.rejected,
  rejectedBy: k.rejected_by,
  rejectedAt: k.rejected_at,
  rejectionReason: k.rejection_reason,
  consentGivenAt: k.consent_given_at,
  createdAt: k.created_at,
  updatedAt: k.updated_at,
})

export class KycRepositoryImpl implements KycRepository {
  public async getKycRecords(filters: GetKycFilters): Promise<GetKycResponse> {
    try {
      const res = await kycApiDatasource.getKycRequests({
        skip: (filters.page - 1) * filters.limit,
        limit: filters.limit,
        pending: filters.status === 'pending' ? true : undefined,
        verified: filters.status === 'verified' ? true : undefined,
        rejected: filters.status === 'rejected' ? true : undefined,
      })

      return {
        records: res.data.map(toDomainKyc),
        totalItems: res.total,
        totalPages: Math.max(1, Math.ceil(res.total / filters.limit)),
      }
    } catch (err) {
      throw new Error(extractErrorMessage(err, 'Failed to load KYC records.'))
    }
  }

  public async getSensitiveDetails(kycId: string): Promise<KycSensitiveDetails> {
    try {
      const res = await kycApiDatasource.getSensitiveDetails(kycId)
      return {
        name: res.name,
        phone: res.phone,
        email: res.email,
        address: res.address,
        pan: res.pan,
        gstin: res.gstin,
      }
    } catch (err) {
      throw new Error(extractErrorMessage(err, 'Failed to load KYC sensitive details.'))
    }
  }

  public async getDocumentUrl(kycId: string, docType: KycDocType): Promise<string> {
    try {
      const res = await kycApiDatasource.getDocumentUrl(kycId, docType)
      return res.url
    } catch (err) {
      throw new Error(extractErrorMessage(err, 'Failed to load KYC document.'))
    }
  }

  public async verifyKyc(kycId: string): Promise<KycListItem> {
    try {
      const updated = await kycApiDatasource.verifyKyc(kycId)
      return toDomainKyc(updated)
    } catch (err) {
      throw new Error(extractErrorMessage(err, 'Failed to approve KYC.'))
    }
  }

  public async rejectKyc(kycId: string, reason: string): Promise<KycListItem> {
    try {
      const updated = await kycApiDatasource.rejectKyc(kycId, reason)
      return toDomainKyc(updated)
    } catch (err) {
      throw new Error(extractErrorMessage(err, 'Failed to reject KYC.'))
    }
  }
}

export const kycRepository = new KycRepositoryImpl()
